import { storedDateToDay } from '../../utils/storedDate'

// Wiens rooster de agenda toont, en tot wanneer. Nodig naast `visibleAgendaDocs`
// omdat die alleen een lijst ID's kent: de grens per deelnemer (de
// uitstroomdatum) moet ook naar de weergave, en die rekent elders de reeks uit.
// Updateverzoek Mijn Buuf, 2026-10-15: een uitgestroomde deelnemer bleef in de
// agenda staan.

/**
 * De uitstroomdatum als lokale dag (00:00), of null.
 *
 * `Personas.OutflowDate` heeft in productie drie vormen: een Timestamp, een
 * 'yyyy-mm-dd'-tekst uit een datumveld, en een lege tekst.
 *
 * @param {object|null|undefined} persona
 * @returns {Date|null}
 */
export const personaOutflowDay = (persona) => storedDateToDay(persona?.OutflowDate)

/**
 * Ligt dit moment ná de laatste roosterdag? De dag zelf telt nog mee.
 *
 * @param {Date|null|undefined} date - Begin van een roosterblok
 * @param {Date|null|undefined} lastDay - Laatste roosterdag (lokaal, 00:00)
 * @returns {boolean}
 */
export const isAfterLastDay = (date, lastDay) => {
    if (!(date instanceof Date) || !(lastDay instanceof Date)) return false
    return date >= new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1)
}

/**
 * @typedef {object} AgendaSchedulePersonas
 * @property {Array<object>} personas - Deelnemers van wie het rooster in de agenda hoort
 * @property {Record<string, Date>} endById - Per deelnemer-`ID` de laatste dag van het rooster
 */

/**
 * Een actieve deelnemer staat met zijn hele rooster in de agenda. Een
 * uitgestroomde deelnemer met een uitstroomdatum ook, maar tot en met die dag.
 * Elke andere deelnemer blijft weg: een inactieve, of een uitgestroomde zonder
 * datum, daar valt geen grens te trekken.
 *
 * De datum telt alleen bij de status 'Uitgestroomd'. Wie terug op actief gaat
 * houdt zijn oude uitstroomdatum als geschiedenis (besluit Gijs, 2026-10-15),
 * en die mag zijn rooster dan niet meer stoppen.
 *
 * Wie al vóór `periodStart` uitstroomde valt helemaal weg: in die periode heeft
 * hij geen roosterblok, dus ook geen regel in de roosterweergave en geen
 * afwezigheid die opgehaald moet worden. Zonder die grens groeit de lijst met
 * iedereen die ooit uitstroomde.
 *
 * @param {Array<object>|null|undefined} personas - Alle deelnemers
 * @param {Date} [periodStart] - Begin van de getoonde periode
 * @returns {AgendaSchedulePersonas}
 */
const agendaSchedulePersonas = (personas, periodStart) => {
    /** @type {Record<string, Date>} */
    const endById = {}
    const mee = (Array.isArray(personas) ? personas : []).filter((persona) => {
        if (persona?.Status === 'active') return true
        if (persona?.Status !== 'outflow') return false
        const end = personaOutflowDay(persona)
        if (!end || isAfterLastDay(periodStart, end)) return false
        if (persona?.ID) endById[persona.ID] = end
        return true
    })
    return { personas: mee, endById }
}

/**
 * Hoort dit roosterblok nog in beeld? Eén plek voor de agenda (CalendarView) en
 * de tijdkiezer bij het inplannen (CalendarSlotPicker).
 *
 * @param {object} task - Een taak uit `Tasks`
 * @param {Record<string, Date>} endById - Laatste roosterdag per deelnemer
 * @returns {{lastDay: (Date|undefined), past: boolean}} `lastDay` voor de einddatum van een reeks;
 *   `past` als een los blok (zonder herhaling) ná die dag valt en dus weg hoort
 */
export const scheduleCutoff = (task, endById) => {
    const lastDay = task?.Type === 'personaSchedule' ? endById?.[task.PersonaID] : undefined
    const past = Boolean(lastDay) && !task?.rrule && isAfterLastDay(task?.Timestamp?.toDate?.(), lastDay)
    return { lastDay, past }
}

/**
 * Roosterregels met de uitstroomdag als einddatum (`EffectiveUntil`), voor de
 * controles die per dag kijken of iemand ingeroosterd is — de
 * buiten-roosterwaarschuwing. Een eerdere eigen 'T/M' blijft staan.
 *
 * Geeft KOPIEËN terug, alleen voor die controles: de regels die de agenda
 * opent en opslaat blijven de originelen, anders zou een opslag de uitstroomdag
 * als 'T/M' wegschrijven.
 *
 * @param {Array<object>} schedules - Roosterregels (`Tasks` met `Type: 'personaSchedule'`)
 * @param {Record<string, Date>} endById - Laatste roosterdag per deelnemer
 * @returns {Array<object>}
 */
export const withScheduleEnd = (schedules, endById) => (Array.isArray(schedules) ? schedules : []).map((schedule) => {
    const lastDay = endById?.[schedule?.PersonaID]
    if (!(lastDay instanceof Date)) return schedule
    const own = schedule?.EffectiveUntil?.toDate?.()
    if (own instanceof Date && own <= lastDay) return schedule
    return { ...schedule, EffectiveUntil: { toDate: () => lastDay } }
})

export default agendaSchedulePersonas
